import { computed, Injectable, Signal, signal } from '@angular/core';

import { Portfolio } from '../business/api/portfolio/portfolio';
import { Brand } from '../business/context/brand.enum';

/**
 * Centralized service for managing portfolio-related flags throughout the ECD application.
 *
 * This service provides a single source of truth for portfolio flags (Sosh, Orange ABBA, etc.)
 * and user identity flags, exposed as reactive Signals with optional Observable compatibility.
 *
 * **Flags exposed:**
 * - `isSosh`: True if all user contracts are Sosh brand
 * - `isFullAbba`: True if user is eligible for full ABBA (All-Bandwidth Bundle)
 * - `isLoggedWithOpenCaraibe`: True if user is logged with Orange Caraïbe
 * - `isEligibleToBloom`: True if user qualifies for Bloom advantages
 * - `isVerified`: True if user's identity is verified
 * - `isPremium`: True if user has premium subscription
 * - `hasInvalidContactMeans`: True if user has invalid contact information
 *
 * **Synchronization:**
 * Call `syncFromPortfolio(portfolio)` to update all flags from Portfolio API response.
 * Typically called automatically by PortfolioService on each `getPortfolio()` call via tap().
 *
 * @example
 * ```typescript
 * // Inject the service
 * private readonly portfolioFlags = inject(PortfolioFlagsService);
 *
 * // Use signals (modern Angular pattern)
 * const isSosh = this.portfolioFlags.isSosh();
 * effect(() => {
 *   if (this.portfolioFlags.isSosh()) {
 *     console.log('User is Sosh');
 *   }
 * });
 * ```
 *
 * @Injectable providedIn: 'root' - Singleton service
 */
@Injectable({ providedIn: 'root' })
export class PortfolioFlagsService {
    // ========================
    // Private Signals (writable)
    // ========================

    /** True if all user contracts are Sosh brand */
    private readonly _isSosh = signal<boolean>(false);

    /** True if user is eligible for full ABBA */
    private readonly _isFullAbba = signal<boolean>(false);

    /** True if user is logged with Orange Caraïbe */
    private readonly _isLoggedWithOpenCaraibe = signal<boolean>(false);

    /** True if user qualifies for Bloom advantages */
    private readonly _isEligibleToBloom = signal<boolean>(false);

    /** True if user's identity is verified (default: true) */
    private readonly _isVerified = signal<boolean>(true);

    /** True if user has premium subscription */
    private readonly _isPremium = signal<boolean>(false);

    /** True if user has invalid contact information */
    private readonly _hasInvalidContactMeans = signal<boolean>(false);

    // ========================
    // Public Read-only Signals
    // ========================

    /**
     * Signal: True if all user contracts are Sosh brand.
     * Computed from portfolio contracts where all contracts.brand === 'Sosh'.
     *
     * @readonly
     * @example
     * ```typescript
     * const isSosh = effect(() => portfolioFlags.isSosh());
     * ```
     */
    readonly isSosh: Signal<boolean> = this._isSosh.asReadonly();

    /**
     * Signal: True if user is eligible for full ABBA (All-Bandwidth Bundle).
     * Set from `portfolio.identity.isFullAbba`.
     *
     * @readonly
     */
    readonly isFullAbba: Signal<boolean> = this._isFullAbba.asReadonly();

    /**
     * Signal: True if user is logged with Orange Caraïbe (Caribbean Orange).
     * Set from `portfolio.identity.isLoggedWithOpenCaraibe`.
     *
     * @readonly
     */
    readonly isLoggedWithOpenCaraibe: Signal<boolean> = this._isLoggedWithOpenCaraibe.asReadonly();

    /**
     * Signal: True if user qualifies for Bloom advantages.
     * Set from `portfolio.identity.isEligibleToBloom`.
     *
     * @readonly
     */
    readonly isEligibleToBloom: Signal<boolean> = this._isEligibleToBloom.asReadonly();

    /**
     * Signal: True if user's identity is verified.
     * Set from `portfolio.identity.isVerified` (defaults to true).
     *
     * @readonly
     */
    readonly isVerified: Signal<boolean> = this._isVerified.asReadonly();

    /**
     * Signal: True if user has premium subscription.
     * Set from `portfolio.identity.isPremium`.
     *
     * @readonly
     */
    readonly isPremium: Signal<boolean> = this._isPremium.asReadonly();

    /**
     * Signal: True if user has invalid contact information.
     * Set from `portfolio.identity.hasInvalidContactMeans`.
     *
     * @readonly
     */
    readonly hasInvalidContactMeans: Signal<boolean> = this._hasInvalidContactMeans.asReadonly();

    // ========================
    // Computed Signals
    // ========================

    /**
     * Computed Signal: True if user is Orange (derived from isSosh).
     * Convenience computed signal: isOrange = !isSosh.
     *
     * @readonly
     */
    readonly isOrange = computed(() => !this.isSosh());

    // ========================
    // Public Methods
    // ========================

    /**
     * Synchronize all portfolio flags from a Portfolio API response.
     *
     * **isSosh calculation:**
     * - True if ALL contracts in portfolio have brand === 'Sosh' (case-insensitive)
     * - False if portfolio is empty, null, or contains mixed/Orange brands
     *
     * **Identity flags:**
     * - Pulled directly from portfolio.identity fields with sensible defaults
     * - isVerified defaults to true if undefined
     * - All others default to false
     *
     * This method is idempotent: calling it multiple times with the same portfolio
     * data will always produce the same signal state.
     *
     * @param portfolio Portfolio object from API response containing contracts and identity
     *
     * @example
     * ```typescript
     * portfolioService.getPortfolio().subscribe(portfolio => {
     *   portfolioFlags.syncFromPortfolio(portfolio);
     * });
     * ```
     */
    public syncFromPortfolio(portfolio: Portfolio | null | undefined): void {
        // Calculate isSosh: true only if ALL contracts are Sosh
        const isSosh =
            portfolio?.contracts && portfolio.contracts.length > 0
                ? portfolio.contracts.every((contract) => contract.brand?.toUpperCase() === Brand.SOSH)
                : false;

        this._isSosh.set(isSosh);

        // Pull flags from identity (with sensible defaults)
        const identity = portfolio?.identity;
        this._isFullAbba.set(identity?.isFullAbba ?? false);
        this._isLoggedWithOpenCaraibe.set(identity?.isLoggedWithOpenCaraibe ?? false);
        this._isEligibleToBloom.set(identity?.isEligibleToBloom ?? false);
        this._isVerified.set(identity?.isVerified ?? true); // Default: verified
        this._isPremium.set(identity?.isPremium ?? false);
        this._hasInvalidContactMeans.set(identity?.hasInvalidContactMeans ?? false);
    }

    /**
     * Reset all flags to their default values.
     *
     * Useful for logout or clearing user state.
     *
     * **Default values:**
     * - Sosh flags: false
     * - isVerified: true (assume safe default)
     * - All others: false
     *
     * @example
     * ```typescript
     * authService.logout$.subscribe(() => {
     *   portfolioFlags.reset();
     * });
     * ```
     */
    public reset(): void {
        this._isSosh.set(false);
        this._isFullAbba.set(false);
        this._isLoggedWithOpenCaraibe.set(false);
        this._isEligibleToBloom.set(false);
        this._isVerified.set(true); // Safe default
        this._isPremium.set(false);
        this._hasInvalidContactMeans.set(false);
    }

    /**
     * Manually set the isSosh flag.
     *
     * Used when the brand changes dynamically (e.g., when user selects a different offer
     * in the carrousel). Typically, this is called automatically via `syncFromPortfolio()`,
     * but this method allows for imperative updates when needed.
     *
     * @param isSosh Boolean value to set the isSosh flag to
     *
     * @example
     * ```typescript
     * // When user selects a Sosh offer
     * portfolioFlags.setIsSosh(true);
     * ```
     */
    public setIsSosh(isSosh: boolean): void {
        this._isSosh.set(isSosh);
    }
}
